import type { RowMenuItem } from '@lite-workspace/shared/blocks/row-menu';
import type { ProjectListItem } from '@lite-workspace/shared/blocks/list';
import {
  demoBuildState,
  formatShortDate,
  isDemoLocked,
  isDraftProject,
  masterAlbumPresence,
  studentAlbumsFraction,
  type DemoBuildState,
} from '../domain/utils/project.utils';
import type { FC_ProjectDTO } from '../domain/entities/project.models';

/** Перезапуск сборки демо-копии прямо из списка — второй путь к тому же вызову, помимо окна гейта. */
export const DEMO_RETRY_ACTION = 'demo-retry';

/** Подпись одна на меню строки, кнопку карточки и окно гейта — расхождение читалось бы как разные действия. */
export const DEMO_RETRY_LABEL = 'Попробовать снова';

/** Нет шаблона или пак удалён — проект свёрстан свободным дизайном. */
const FREE_DESIGN_LABEL = 'Свободный дизайн';

const DEMO_NOTE: Record<DemoBuildState, string> = {
  preparing: 'Готовим демо-проект…',
  failed: 'Ошибка подготовки',
};

export const CHANGE_TEMPLATE_ACTION = 'change-template';

const RENAME_ITEM: RowMenuItem = { id: 'rename', label: 'Переименовать', icon: 'pencil' };
const CHANGE_TEMPLATE_ITEM: RowMenuItem = { id: CHANGE_TEMPLATE_ACTION, label: 'Сменить шаблон', icon: 'brush' };
/** Прогон идёт минутами уже без вуали на карточке — выключенный пункт сам называет причину. */
const CHANGE_TEMPLATE_LOCKED: RowMenuItem = {
  ...CHANGE_TEMPLATE_ITEM,
  disabled: true,
  disabledReason: 'Дождитесь завершения текущего применения шаблона',
};

export const PUT_AWAY_ACTION = 'put-away';
export const RESTORE_WORK_ACTION = 'restore-work';

/** «Удалить» на активных кладёт проект в корзину; безвозвратно удаляет только «Удалить навсегда» из корзины. */
const DELETE_ITEM: RowMenuItem = { id: 'delete', label: 'Удалить', icon: 'trash', danger: true };

const ACTIVE_TAIL: RowMenuItem[] = [
  { id: 'multiselect', label: 'Мультивыбор', icon: 'check' },
  { id: PUT_AWAY_ACTION, label: 'В архив', icon: 'archive-box' },
  DELETE_ITEM,
];

/** «Архив»: the project still works, so the menu only moves it back or to the trash. */
const PUT_AWAY_MENU: RowMenuItem[] = [
  { id: RESTORE_WORK_ACTION, label: 'Вернуть в работу', icon: 'archive-box-up' },
  DELETE_ITEM,
];

/** Смена шаблона на проекте: одна живёт в прогоне — её карточка занята, новая смена ждёт конца. */
export interface ProjectApplyRun {
  readonly running: boolean;
  readonly busy: boolean;
}

const NO_RUN: ProjectApplyRun = { running: false, busy: false };

// Шаблон проекта несёт эталонный альбом — без него (черновик, альбомы ещё не собраны) менять нечего.
function activeMenu(project: FC_ProjectDTO, run: ProjectApplyRun): RowMenuItem[] {
  if (masterAlbumPresence(project) === 'absent') return [RENAME_ITEM, ...ACTIVE_TAIL];
  return [RENAME_ITEM, run.running ? CHANGE_TEMPLATE_LOCKED : CHANGE_TEMPLATE_ITEM, ...ACTIVE_TAIL];
}

const ARCHIVED_MENU: RowMenuItem[] = [
  { id: 'restore', label: 'Восстановить', icon: 'archive-box-up' },
  { id: 'purge', label: 'Удалить навсегда', icon: 'trash', danger: true },
];

/**
 * Fotoclass's own reading of a project for the shared list: what a count means, how a date reads and
 * which actions an archived project still offers. The table cell always shows a figure, falling back
 * to the plain photobook total; the card pill only shows the student fraction, so a project the
 * backend cannot yet split by student keeps its bare date instead of a misleading number.
 */
export function toProjectListItem(project: FC_ProjectDTO, archived: boolean, run: ProjectApplyRun = NO_RUN): ProjectListItem {
  const fraction = studentAlbumsFraction(project);
  // Ожидание демо-копии видно здесь, в списке, а не внутри проекта. Ссылка при этом ОСТАЁТСЯ: мёртвый
  // клик хуже отказа — пользователь жмёт проект, и не происходит ничего. Переход всегда стартует, а
  // останавливает его demoProjectReadyGuard, показывая модалку с объяснением и «Попробовать снова».
  const demoState = demoBuildState(project);
  const baseMenu = archived ? ARCHIVED_MENU : project.putAwayAt ? PUT_AWAY_MENU : activeMenu(project, run);
  const retryItem: RowMenuItem = { id: DEMO_RETRY_ACTION, label: DEMO_RETRY_LABEL, icon: 'cycle' };
  const menuItems = demoState === 'failed' ? [retryItem, ...baseMenu] : baseMenu;

  return {
    id: project.id,
    name: project.name,
    // Две независимые подписи, а не одна на два смысла: слово «Demo» — это ЧТО за проект, и оно стоит
    // рядом с именем всегда; вердикт сборки демо-копии — это КАК он сейчас готовится, и он живёт под
    // именем только пока готовится или сорвался. Раньше `nameNote` нёс оба, и на неготовом демо слово
    // «Demo» подменялось словом «Готовим…» — признак типа проекта пропадал ровно тогда, когда он нужен.
    badge: isDemoLocked(project) ? 'Demo' : undefined,
    nameNote: demoState ? DEMO_NOTE[demoState] : undefined,
    routerLink: ['/projects', project.id],
    createdLabel: formatShortDate(project.createdAt),
    countLabel: fraction ?? `${project.counter.photobooks}`,
    cardCountLabel: fraction ?? undefined,
    coverUrl: project.coverUrl,
    // Absent = the gateway could not resolve the pack: no caption beats a false «Свободный дизайн».
    templateName: project.templateName === null ? FREE_DESIGN_LABEL : project.templateName,
    busy: run.busy || undefined,
    draft: isDraftProject(project) || undefined,
    menuItems,
  };
}
